import { Container, EmptyState, Flex, Table, VStack } from "@chakra-ui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { FiSearch } from "react-icons/fi";

import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from "@/components/ui/pagination";

import { TablesService, TableFieldsService } from "@/client";
import type { TableFieldPublic } from "@/client";
import { ActionsMenu } from "@/components/Common/ActionsMenu";
import AddTableField from "@/components/TableFields/AddTableField";
import PendingItems from "@/components/Pending/PendingItems";
import { Select } from "@/components/ui/select";
import { ProcessStateTag } from "@/components/ui/state-tag";

const PER_PAGE = 5;

const fieldsSearchSchema = z.object({
  page: z.number().catch(1),
});

export const Route = createFileRoute("/_layout/tableFields")({
  component: TableFields,
  validateSearch: (search) => fieldsSearchSchema.parse(search),
});

function getTableFieldsQueryOptions({
  tableId,
  page,
}: {
  tableId: string;
  page: number;
}) {
  return {
    queryFn: () =>
      TableFieldsService.readTableFields({
        tableId,
        skip: (page - 1) * PER_PAGE,
        limit: PER_PAGE,
      }),
    queryKey: ["tableFields", { tableId, page }],
  };
}

function TableFieldsTable({
  selectedTableId,
}: {
  selectedTableId: string | null;
}) {
  const { page } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const queryClient = useQueryClient();
  const setPage = (page: number) =>
    navigate({
      search: (prev: { [key: string]: string }) => ({ ...prev, page }),
    });

  const { data, isLoading, isPlaceholderData } = useQuery({
    ...getTableFieldsQueryOptions({ tableId: selectedTableId || "", page }),
    enabled: !!selectedTableId,
    placeholderData: (prevData) => prevData,
  });

  const tableFields = (data?.data ?? []) as unknown as TableFieldPublic[];
  const count = data?.count ?? 0;

  useEffect(() => {
    if (selectedTableId && tableFields.length === PER_PAGE) {
      queryClient.prefetchQuery(
        getTableFieldsQueryOptions({ tableId: selectedTableId, page: page + 1 })
      );
    }
  }, [page, tableFields.length, selectedTableId, queryClient]);

  if (isLoading) return <PendingItems />;

  if (!tableFields.length) {
    return (
      <EmptyState.Root>
        <EmptyState.Content>
          <EmptyState.Indicator>
            <FiSearch />
          </EmptyState.Indicator>
          <VStack textAlign="center">
            <EmptyState.Title>필드가 없습니다</EmptyState.Title>
            <EmptyState.Description>
              테이블을 선택한 후 새 필드를 추가해 시작할 수 있습니다
            </EmptyState.Description>
          </VStack>
        </EmptyState.Content>
      </EmptyState.Root>
    );
  }

  return (
    <>
      <Table.Root size={{ base: "sm", md: "md" }}>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>Index</Table.ColumnHeader>
            <Table.ColumnHeader>필드 명</Table.ColumnHeader>
            <Table.ColumnHeader>설명</Table.ColumnHeader>
            <Table.ColumnHeader>코드명</Table.ColumnHeader>
            <Table.ColumnHeader>필수</Table.ColumnHeader>
            <Table.ColumnHeader>필드 타입</Table.ColumnHeader>
            <Table.ColumnHeader>메타데이터</Table.ColumnHeader>
            <Table.ColumnHeader>상태</Table.ColumnHeader>
            <Table.ColumnHeader>수정</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {tableFields.map((tableField) => (
            <Table.Row
              key={tableField.id}
              opacity={isPlaceholderData ? 0.5 : 1}
            >
              <Table.Cell>{tableField.index}</Table.Cell>
              <Table.Cell>{tableField.field_name}</Table.Cell>
              <Table.Cell>{tableField.description}</Table.Cell>
              <Table.Cell>{tableField.field_code}</Table.Cell>
              <Table.Cell>{tableField.is_required ? "Yes" : "No"}</Table.Cell>
              <Table.Cell>{tableField.field_type}</Table.Cell>
              <Table.Cell>
                {tableField.field_type === "number" &&
                  tableField.number_metadata && (
                    <>
                      최소값: {tableField.number_metadata.min_value ?? ""}
                      <br />
                      최대값: {tableField.number_metadata.max_value ?? ""}
                      <br />
                      숫자 타입: {tableField.number_metadata.number_type ?? ""}
                      <br />
                      소수점 반올림:{" "}
                      {tableField.number_metadata.number_type === "decimal"
                        ? tableField.number_metadata.decimal_round ?? ""
                        : "N/A"}
                      <br />
                      단위: {tableField.number_metadata.unit ?? ""}
                    </>
                  )}
                {tableField.field_type === "string" &&
                  tableField.string_metadata && (
                    <>
                      문자열 타입:{" "}
                      {tableField.string_metadata.string_type || ""}
                    </>
                  )}
                {tableField.field_type === "datetime" &&
                  tableField.datetime_metadata && (
                    <>
                      날짜/시간 타입:{" "}
                      {tableField.datetime_metadata.datetime_type || ""}
                    </>
                  )}
                {tableField.field_type === "selection" &&
                  tableField.selection_metadata && (
                    <ul style={{ paddingLeft: 16 }}>
                      {(
                        tableField.selection_metadata.options as Array<{
                          label: string;
                          color: string;
                        }>
                      )?.map((opt, i) => (
                        <li key={`${tableField.id}-option-${i}-${opt.label}`}>
                          <span style={{ marginRight: 8 }}>{opt.label}</span>
                          <span
                            style={{
                              display: "inline-block",
                              width: 12,
                              height: 12,
                              backgroundColor: opt.color ?? "#ccc",
                              borderRadius: "50%",
                            }}
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                {tableField.field_type === "file" &&
                  tableField.file_metadata && (
                    <>
                      허용 확장자:{" "}
                      {tableField.file_metadata.allowed_extension || ""} <br />
                      최대 용량:{" "}
                      {tableField.file_metadata.max_file_size_mb || ""} MB
                    </>
                  )}
              </Table.Cell>
              <Table.Cell>
                <ProcessStateTag
                  state={tableField?.process_state || "IDLE"}
                  _disabled={{ opacity: 0.5 }}
                />
              </Table.Cell>
              <Table.Cell>
                <ActionsMenu
                  tableField={tableField}
                  currentPage={page}
                  totalCount={count}
                  onPageChange={(newPage) => setPage(newPage)}
                />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>

      <Flex justifyContent="flex-end" mt={4}>
        <PaginationRoot
          count={count}
          pageSize={PER_PAGE}
          onPageChange={({ page }) => setPage(page)}
        >
          <Flex>
            <PaginationPrevTrigger />
            <PaginationItems />
            <PaginationNextTrigger />
          </Flex>
        </PaginationRoot>
      </Flex>
    </>
  );
}

async function fetchTables() {
  return await TablesService.readTables();
}

function TableFields() {
  const [selectedTableId, setSelectedTableId] = useState("");

  const { data: tables } = useQuery({
    queryKey: ["tables"],
    queryFn: fetchTables,
  });

  return (
    <Container maxW="full">
      <Select
        name="table_id"
        // label="테이블"
        placeholder="테이블 선택"
        value={selectedTableId}
        onChange={(val) => setSelectedTableId(val)}
        options={
          tables?.data.map((table) => ({
            label: table.table_name,
            value: table.id,
          })) || []
        }
        isRequired
      />
      <AddTableField tableId={selectedTableId} />

      {/* <Navbar
        type="TableField"
        addModalAs={AddTableField}
        isDisabled={!selectedTableId}
        selectedTableId={selectedTableId}
      /> */}

      <TableFieldsTable selectedTableId={selectedTableId} />
    </Container>
  );
}

export default TableFields;
